import { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useCreateScreenlockPolicy, useUpdateScreenlockPolicy } from "@/api/devices";
import type { DeviceScreenlockPolicy } from "@/api/types";

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  deviceId: string;
  policy?: DeviceScreenlockPolicy | null;
}

export function ScreenlockPolicyModal({ open, onClose, tenantId, deviceId, policy }: Props) {
  const isEdit = !!policy;
  const create = useCreateScreenlockPolicy(tenantId, deviceId);
  const update = useUpdateScreenlockPolicy(tenantId, deviceId);
  const isPending = create.isPending || update.isPending;

  const [requiresPassword, setRequiresPassword] = useState(false);
  const [timeoutMs, setTimeoutMs] = useState(0);

  useEffect(() => {
    if (open) {
      setRequiresPassword(policy?.requires_password ?? false);
      setTimeoutMs(policy?.screen_sleep_timeout_ms ?? 0);
    }
  }, [open, policy]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { requires_password: requiresPassword, screen_sleep_timeout_ms: timeoutMs };
    const onSuccess = { onSuccess: onClose };
    if (isEdit) {
      update.mutate({ id: policy.id, payload }, onSuccess);
    } else {
      create.mutate({ payload }, onSuccess);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? "Edit" : "Add"} Screenlock Policy</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="text-sm text-text-muted">Requires Password</label>
          <Switch checked={requiresPassword} onChange={setRequiresPassword} />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Screen Sleep Timeout (ms)</label>
          <Input type="number" min={0} value={timeoutMs} onChange={e => setTimeoutMs(Number(e.target.value))} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button type="submit" disabled={isPending}>{isPending ? "Saving..." : isEdit ? "Update" : "Add"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
